import { useInfiniteQuery, UseInfiniteQueryResult } from 'react-query';
import pDebounce from 'p-debounce';

import { useAuth } from 'src/hooks/useAuth';

import { getAccountList } from 'src/utils/urls';
import { trimValues } from 'src/utils/common';
import { accountSortConstants } from 'src/utils/sortConstants';
import { getColumnFromId } from 'src/utils/filterHelper';

import { AccountInfo } from 'src/types/AccountInfo';

export const pageLength = 100;

async function fetchAccountList({
  requestData,
  pageParam,
  token,
  tokenType,
}: TVariables & { pageParam: number }): Promise<TPage> {
  requestData.append('start', `${pageParam}`);
  const response = await fetch(getAccountList, {
    method: 'POST',
    headers: {
      Authorization: ` ${tokenType} ${token}`,
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: requestData,
  });
  return response.json();
}

const debouncedFetch = pDebounce(fetchAccountList, 500);

export type TResult = {
  pages: Array<TPage>;
};

export type TPage = {
  Data: Array<AccountInfo>;
  Draw: number;
  error: Error | null;
  FilteredREcords: number;
  TotalRecords: number;
  IsSuccess: boolean;
  IsAutoRefreshEnabled: boolean;
  StatusCode: number;
};

export type TError = { message: string };

export type TVariables = {
  requestData: any;
  token: string;
  tokenType: string;
};

function setRequestData({ columnsSearch, currentSortItem }: inputType): any {
  const formData = new URLSearchParams();
  formData.append('draw', '1');
  formData.append('length', `${100}`);
  formData.append('columns[0][data]', 'AccountStatusFilter');
  formData.append('columns[0][name]', '');
  formData.append('columns[0][searchable]', 'true');
  formData.append('columns[0][orderable]', 'true');
  formData.append('columns[0][search][regex]', 'false');
  formData.append('columns[0][search][value]', '');

  formData.append('columns[1][data]', 'function');
  formData.append('columns[1][name]', 'VDN,ShortAnswerScript');
  formData.append('columns[1][searchable]', 'true');
  formData.append('columns[1][orderable]', 'true');
  formData.append('columns[1][search][regex]', 'false');
  formData.append('columns[1][search][value]', '');

  formData.append('columns[2][data]', 'CID');
  formData.append('columns[2][name]', '');
  formData.append('columns[2][searchable]', 'true');
  formData.append('columns[2][orderable]', 'true');
  formData.append('columns[2][search][regex]', 'false');
  formData.append(
    'columns[2][search][value]',
    columnsSearch?.CID ? trimValues(columnsSearch?.CID) : '',
  );

  formData.append('columns[3][data]', 'SID');
  formData.append('columns[3][name]', '');
  formData.append('columns[3][searchable]', 'true');
  formData.append('columns[3][orderable]', 'true');
  formData.append('columns[3][search][regex]', 'false');
  formData.append(
    'columns[3][search][value]',
    columnsSearch?.SID ? trimValues(columnsSearch?.SID) : '',
  );

  formData.append('columns[4][data]', 'PID');
  formData.append('columns[4][name]', '');
  formData.append('columns[4][searchable]', 'true');
  formData.append('columns[4][orderable]', 'true');
  formData.append('columns[4][search][regex]', 'false');
  formData.append(
    'columns[4][search][value]',
    columnsSearch?.PID ? trimValues(columnsSearch?.PID) : '',
  );

  formData.append('columns[5][data]', 'AnswerScript');
  formData.append('columns[5][name]', '');
  formData.append('columns[5][searchable]', 'true');
  formData.append('columns[5][orderable]', 'true');
  formData.append('columns[5][search][regex]', 'false');
  formData.append('columns[5][search][value]', '');

  formData.append('columns[6][data]', 'Name');
  formData.append('columns[6][name]', '');
  formData.append('columns[6][searchable]', 'true');
  formData.append('columns[6][orderable]', 'true');
  formData.append('columns[6][search][regex]', 'false');
  formData.append(
    'columns[6][search][value]',
    columnsSearch?.AccountName ? trimValues(columnsSearch?.AccountName) : '',
  );

  formData.append('columns[7][data]', 'RelationshipManager');
  formData.append(
    'columns[7][name]',
    'RelationshipManager.FirstName,RelationshipManager.LastName',
  );
  formData.append('columns[7][searchable]', 'true');
  formData.append('columns[7][orderable]', 'true');
  formData.append('columns[7][search][regex]', 'false');
  formData.append('columns[7][search][value]', '');

  formData.append('columns[8][data]', 'City');
  formData.append('columns[8][name]', '');
  formData.append('columns[8][searchable]', 'true');
  formData.append('columns[8][orderable]', 'true');
  formData.append('columns[8][search][regex]', 'false');
  formData.append('columns[8][search][value]', '');

  formData.append('columns[9][data]', 'StateProvince');
  formData.append('columns[9][name]', 'StateProvince.Abbreviation');
  formData.append('columns[9][searchable]', 'true');
  formData.append('columns[9][orderable]', 'true');
  formData.append('columns[9][search][regex]', 'false');
  formData.append('columns[9][search][value]', '');

  formData.append('columns[10][data]', 'TimeZone');
  formData.append('columns[10][name]', 'TimeZone.Abbreviation');
  formData.append('columns[10][searchable]', 'true');
  formData.append('columns[10][orderable]', 'true');
  formData.append('columns[10][search][regex]', 'false');
  formData.append(
    'columns[10][search][value]',
    columnsSearch?.Timezone ? trimValues(columnsSearch?.Timezone) : '',
  );

  formData.append(
    'order[0][column]',
    currentSortItem?.sortKey
      ? getColumnFromId(currentSortItem?.sortKey, accountSortConstants)
      : '2',
  );
  formData.append(
    'order[0][dir]',
    currentSortItem?.sortOrder ? currentSortItem?.sortOrder : 'asc',
  );
  formData.append('search[value]', ' ');
  formData.append('search[regex]', 'false');

  return formData;
}

type inputType = {
  columnsSearch: Record<string, string>;
  currentSortItem: { sortKey: string; sortOrder: string };
};

function useGetAccountList({
  columnsSearch,
  currentSortItem,
}: inputType): UseInfiniteQueryResult<TPage, unknown> {
  const { token, tokenType } = useAuth();
  return useInfiniteQuery(
    ['get_account_log_list', columnsSearch, currentSortItem],
    ({ pageParam = 0 }) => {
      const requestData = setRequestData({ columnsSearch, currentSortItem });
      return debouncedFetch({ requestData, pageParam, token, tokenType });
    },
    {
      getNextPageParam: (lastPage: TPage, allPages: Array<TPage>) => {
        if (lastPage !== null && lastPage?.Data?.length === pageLength)
          return allPages?.length * pageLength;
      },
      getPreviousPageParam: (firstPage: TPage, allPages: Array<TPage>) => {
        if (firstPage !== null && firstPage.Data?.length === pageLength)
          return allPages?.length - 1 * pageLength;
      },
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
      // retry: 10,
    },
  );
}

export { useGetAccountList };
